import { PRESIDENT_ADDRESS_PAGE } from "@config/presidentAddress";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { PresidentAddressIcon } from "@elements/Banner/PresidentAddressIcon";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import BHBasePage, { type BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { useLoaderData } from "react-router";

import LiveBlog from "./Components/LiveBlog";
import PresidentAddressArticleList from "./Components/PresidentAddressArticleList";
import {
  fetchPresidentAddressData,
  PresidentAddressPayloadResponseType,
} from "./PresidentAddress.server";
import PresidentAddressMetaData from "./PresidentAddressMetaData";

const adsSectionName = PRESIDENT_ADDRESS_PAGE;
export function meta() {
  return [getMetaData(PresidentAddressMetaData(adsSectionName).metaTags)];
}

export default function PresidentAddress() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    PresidentAddressPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const { tagsData, liveBlog } = dataLoaderResponse.payload;
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  const basePageProps: BHBasePageProps = {
    uniqueSectionName: adsSectionName,
    pageAdTargetValue: pageAdTargetValue,
    hasLb1Ads: true,
    showNDRBanner: false,
    showPresidentAddressBanner: false,
    bodyWrapperStyle: "my-0 lg:my-0 py-3 lg:py-md",
  };

  return (
    <BHBasePage {...basePageProps}>
      <WidgetWrapper>
        <div>
          <div className="border-b-[1px] border-neutral-500 pb-[10px] md:pb-[20px] mb-[10px] md:mb-[20px]">
            <div className="flex gap-xs items-center">
              <PresidentAddressIcon
                isLive={liveBlog ? true : false}
                variant="heading"
              />
              <Heading
                className="text-text-body text-[18px] md:text-[30px] font-bold font-secondary"
                Element="h1"
              >
                Parlimen Ke-15
              </Heading>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-[40px]">
          <div className="w-full">
            {liveBlog ? (
              <div className="w-full h-full">
                <LiveBlog variant="body" liveBlogContent={liveBlog} />
              </div>
            ) : (
              <PresidentAddressArticleList
                articles={tagsData}
                adsSectionName={adsSectionName}
                variant="body"
              />
            )}
          </div>
          <div className="flex flex-col gap-10 lg:max-w-[300px] flex-1">
            <div className={`flex ${liveBlog ? "h-[600px]" : "h-full"}`}>
              <AdsImu2
                uniqueSectionName={adsSectionName}
                pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
              />
            </div>
            {liveBlog && tagsData.length > 0 && (
              <div className="flex flex-col gap-5">
                <span className="text-[20px] font-secondary text-text-body font-semibold">
                  Artikel
                </span>
                <PresidentAddressArticleList
                  articles={tagsData}
                  adsSectionName={adsSectionName}
                  variant="sidebar"
                />
              </div>
            )}
          </div>
        </div>
      </WidgetWrapper>
    </BHBasePage>
  );
}

export async function loader() {
  const data = await fetchPresidentAddressData();
  const { gaData } = PresidentAddressMetaData(adsSectionName);
  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}
